import { FiltersFormFamily } from 'family/view-models/family-list';
import { SearchPerson } from 'shell/view-models/search-anpr';

export const transformSearchFamilyDataset = (values: FiltersFormFamily): SearchPerson => {
  const subjectData = { ...values };

  const hasSubjectIdMunicipality = subjectData.codeMunicipality || subjectData.codeIstat;

  const transformedFilters: SearchPerson = {
    criteriRicerca:
      hasSubjectIdMunicipality || subjectData.codeAnpr
        ? {
            // Fixed value for request family
            tipoScheda: '1',
            idFamigliaConvivenzaComune: hasSubjectIdMunicipality
              ? {
                  idFamigliaConvivenza: subjectData.codeMunicipality || undefined,
                  idFamigliaConvivenzaComuneIstat: subjectData.codeIstat || undefined,
                }
              : undefined,
            identificativoANPR: subjectData.codeAnpr || undefined,
          }
        : undefined,
    datiRichiesta: {
      dataRiferimentoRichiesta: subjectData.dateForm?.toFormat('yyyy-MM-dd') || undefined,
      // Fixed value for request family
      schedaAnagraficaRichiesta: '1',
      comuneResidenza:
        subjectData.isNationalResearch &&
        (subjectData.provinceOfResidence || subjectData.municipalityOfResidence?.id)
          ? {
              siglaProvinciaIstat: subjectData.municipalityOfResidence?.provinceCode || undefined,
              codiceIstat: subjectData.municipalityOfResidence?.id || undefined,
            }
          : undefined,
      ricercaNazionale: subjectData.isNationalResearch ? '1' : '0',
      // Fixed value for request family
      datiAnagraficiRichiesti: ['02', '10', '21', '22'],
    },
  };

  return transformedFilters;
};

export const transformSearchFamilyDatasetFull = (values: FiltersFormFamily): SearchPerson => {
  const { subjectData, identityCard = {}, residencePermit = {}, searchByFiscalCode } = values;

  // Family-specific fields
  const hasSubjectIdMunicipality = !!values.codeMunicipality || !!values.codeIstat;
  const hasFamilySearch = hasSubjectIdMunicipality || !!values.codeAnpr;

  // Subject data fields
  const birthdayChoice = subjectData?.birthdayChoice;

  const hasMunicipalityFiltersItalian =
    !!subjectData?.municipalityOfBirth?.id ||
    !!subjectData?.provinceOfBirth ||
    !!subjectData?.locality;

  const hasMunicipalityFiltersForeign =
    !!subjectData?.foreignMunicipality?.id || !!subjectData?.foreignStateBirth;

  const hasSubjectIdMunicipalitySearch = !!subjectData?.municipalityId || !!subjectData?.istatCode;

  const hasSearchBirthPlaceFilters =
    hasMunicipalityFiltersItalian ||
    hasMunicipalityFiltersForeign ||
    !!subjectData?.exceptionalBirthplace;

  const hasSearchBirthFilters =
    hasSearchBirthPlaceFilters || (!!subjectData?.birthdayChoice && !!subjectData?.birthday);

  // Identity card fields
  const hasMunicipalityIdentityCard = !!identityCard?.municipality?.id || !!identityCard.province;

  const hasSearchIdentityCard =
    hasMunicipalityIdentityCard || !!identityCard.consulate || !!identityCard.number;

  // Residence permit fields
  const hasSearchPermitReferences = !!residencePermit.permitNumber || !!residencePermit.preccint;

  const hasSearchResidencePermit = hasSearchPermitReferences || !!residencePermit.passportNumber;

  // Common search criteria
  const hasSearchCriteria =
    !!subjectData?.gender ||
    !!searchByFiscalCode?.fiscalCode ||
    !!subjectData?.fiscalCode ||
    !!subjectData?.name ||
    !!subjectData?.surname ||
    !!subjectData?.withoutName ||
    !!subjectData?.withoutSurname ||
    !!subjectData?.anprId;

  const transformedFilters: SearchPerson = {
    criteriRicerca:
      hasFamilySearch ||
      hasSubjectIdMunicipalitySearch ||
      hasSearchResidencePermit ||
      hasSearchCriteria ||
      hasSearchIdentityCard
        ? {
            // Fixed value for family request
            tipoScheda: '1',
            // Family-specific fields
            idFamigliaConvivenzaComune: hasSubjectIdMunicipality
              ? {
                  idFamigliaConvivenza: values.codeMunicipality || undefined,
                  idFamigliaConvivenzaComuneIstat: values.codeIstat || undefined,
                }
              : undefined,
            identificativoANPR: values.codeAnpr || undefined,
            // Common person search fields
            sesso: subjectData?.gender || undefined,
            codiceFiscale: searchByFiscalCode?.fiscalCode || subjectData?.fiscalCode || undefined,
            nome: subjectData?.name || undefined,
            cognome: subjectData?.surname || undefined,
            senzaNome: subjectData?.withoutName ? '1' : undefined,
            senzaCognome: subjectData?.withoutSurname ? '1' : undefined,
            idSchedaSoggettoANPR: subjectData?.anprId || undefined,
            idSchedaSoggettoComune: hasSubjectIdMunicipalitySearch
              ? {
                  idSchedaSoggetto: subjectData?.municipalityId || undefined,
                  idSchedaSoggettoComuneIstat: subjectData?.istatCode || undefined,
                }
              : undefined,
            permessoDiSoggiorno3000: hasSearchResidencePermit
              ? {
                  datiSoggiorno30XX: hasSearchPermitReferences
                    ? {
                        numeroSoggiorno: residencePermit.permitNumber || undefined,
                        questuraRilascioSoggiorno: residencePermit.preccint || undefined,
                      }
                    : undefined,
                  numeroPassaporto: residencePermit.passportNumber || undefined,
                }
              : undefined,
            cartaIdentita3000: hasSearchIdentityCard
              ? {
                  comuneRilascio: hasMunicipalityIdentityCard
                    ? {
                        codiceIstat: identityCard.municipality?.id || undefined,
                        siglaProvinciaIstat: identityCard.municipality?.province || undefined,
                        nomeComune: identityCard.municipality?.description || undefined,
                      }
                    : undefined,
                  codiceConsolatoRilascio: identityCard.consulate || undefined,
                  numero: identityCard.number || undefined,
                }
              : undefined,
          }
        : undefined,
    datiNascita: hasSearchBirthFilters
      ? {
          senzaGiorno:
            !birthdayChoice?.includes('day') && birthdayChoice?.includes('month') ? '1' : undefined,
          senzaGiornoMese:
            !birthdayChoice?.includes('day') &&
            !birthdayChoice?.includes('month') &&
            subjectData?.birthday
              ? '1'
              : undefined,
          dataEvento:
            subjectData?.birthday && subjectData?.birthday?.isValid
              ? subjectData?.birthday.toFormat('yyyy-MM-dd')
              : undefined,
          luogoNascita: hasSearchBirthPlaceFilters
            ? {
                comune: hasMunicipalityFiltersItalian
                  ? {
                      codiceIstat: subjectData?.municipalityOfBirth?.id || undefined,
                      siglaProvinciaIstat: subjectData?.municipalityOfBirth?.province || undefined,
                      nomeComune: subjectData?.municipalityOfBirth?.description || undefined,
                      descrizioneLocalita: subjectData?.locality || undefined,
                    }
                  : undefined,
                localita: hasMunicipalityFiltersForeign
                  ? {
                      codiceStato: subjectData?.foreignStateBirth?.id || undefined,
                      descrizioneStato: subjectData?.foreignStateBirth?.value || undefined,
                      provinciaContea: subjectData?.foreignMunicipality?.province || undefined,
                    }
                  : undefined,
                luogoEccezionale: subjectData?.exceptionalBirthplace || undefined,
              }
            : undefined,
        }
      : undefined,
    datiRichiesta: {
      dataRiferimentoRichiesta: values.dateForm?.toFormat('yyyy-MM-dd') || undefined,
      // Fixed value for family request
      schedaAnagraficaRichiesta: '1',
      comuneResidenza:
        values.isNationalResearch &&
        (values.provinceOfResidence || values.municipalityOfResidence?.id)
          ? {
              siglaProvinciaIstat: values.municipalityOfResidence?.province || undefined,
              codiceIstat: values.municipalityOfResidence?.id || undefined,
            }
          : undefined,
      ricercaNazionale: values.isNationalResearch ? '1' : '0',
      // Fixed value for family request
      datiAnagraficiRichiesti: ['02', '10', '21', '22'],
    },
  };

  return transformedFilters;
};
